import React, { useEffect, useRef, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Card, CardBody, Col, Container, Button, Row } from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { CircularProgress } from "@material-ui/core";
import Sortable from "sortablejs";
import "./style.scss";

// Thin wrapper around vanilla sortablejs (react-sortablejs's bundle uses class-field
// syntax this project's webpack/babel setup can't parse, so we drive Sortable directly).
// No `handle` is set so an item can be grabbed from anywhere; the checkbox is filtered
// out so clicking it toggles the value instead of starting a drag.
const SortableList = ({ onReorder, children }) => {
  const listRef = useRef(null);
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  useEffect(() => {
    if (!listRef.current) return undefined;
    const sortable = Sortable.create(listRef.current, {
      animation: 150,
      filter: "input, label",
      preventOnFilter: false,
      onEnd: (evt) => onReorderRef.current(evt),
    });
    return () => sortable.destroy();
  }, []);

  return (
    <div className="payment-methods-list" ref={listRef}>
      {children}
    </div>
  );
};

// Fixed catalogue of payment methods; the settings API only tells us which keys are shown and in what order.
const PAYMENT_METHODS_MASTER = [
  { id: "grab_otc", Title: "GrabPay", method_key: "grab_otc" },
  { id: "ocbc_paynow", Title: "OCBC PayAnyone", method_key: "ocbc_paynow" },
  { id: "cc", Title: "Credit Card", method_key: "cc" },
  { id: "grabbnpl", Title: "Grab Buy Now Pay Later", method_key: "grabbnpl" },
  { id: "paynowqr", Title: "PayNow QR", method_key: "paynowqr" },
  { id: "lifesg", Title: "LifeSG", method_key: "lifesg" },
  { id: "shppay", Title: "ShopeePay", method_key: "shppay" },
  { id: "shppaylater", Title: "ShopeePayLater", method_key: "shppaylater" },
];

const FGC_PURCHASE_ITEM_NAME = "payment_methods_dgc";
const REWARDS_PURCHASE_ITEM_NAME = "payment_method_rewards";

// item_detail comes back as a Python-style list string, e.g. "['cc','paynowqr']".
const parseItemDetailKeys = (itemDetail) => {
  if (!itemDetail) return [];
  try {
    const parsed = JSON.parse(itemDetail.replace(/'/g, '"'));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// Shown keys keep the order/sort from the API; the rest of the catalogue is appended as hidden.
const buildSectionData = (shownKeys) => {
  const shown = shownKeys
    .map((key, index) => {
      const method = PAYMENT_METHODS_MASTER.find((m) => m.method_key === key);
      return method ? { ...method, sort: index + 1, is_show: true } : null;
    })
    .filter(Boolean);

  const hidden = PAYMENT_METHODS_MASTER.filter(
    (method) => !shownKeys.includes(method.method_key)
  ).map((method, index) => ({
    ...method,
    sort: shown.length + index + 1,
    is_show: false,
  }));

  return [...shown, ...hidden];
};

const formatItemDetail = (data) => {
  const keys = [...data]
    .filter((item) => item.is_show)
    .sort((a, b) => a.sort - b.sort)
    .map((item) => item.method_key);
  return `[${keys.map((key) => `'${key}'`).join(",")}]`;
};

const PaymentMethods = (props) => {
  const [token, setToken] = useState();
  const [loading, setLoading] = useState(true);

  const [fgcPurchaseData, setFgcPurchaseData] = useState([]);
  const [rewardsPurchaseData, setRewardsPurchaseData] = useState([]);
  const [fgcMeta, setFgcMeta] = useState(null);
  const [rewardsMeta, setRewardsMeta] = useState(null);

  const [successAlert, setSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorAlert, setErrorAlert] = useState(false);
  const [loader, setLoader] = useState(false);

  const fetchPaymentMethodsSettings = (authToken) => {
    setLoading(true);
    var myHeaders = new Headers();
    myHeaders.append("Authorization", "Bearer " + authToken);
    myHeaders.append(
      "Cookie",
      "__cfduid=d5b74235daa6f013fde0aba762ec288811620640208"
    );

    var requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(
      `${process.env.REACT_APP_TOKRI_API_URL}/api/frhub?path=settings&env=production`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        const settings = result?.settings || [];
        const fgcItem = settings.find((item) => item.item_name === FGC_PURCHASE_ITEM_NAME);
        const rewardsItem = settings.find((item) => item.item_name === REWARDS_PURCHASE_ITEM_NAME);

        setFgcMeta(fgcItem || null);
        setRewardsMeta(rewardsItem || null);
        setFgcPurchaseData(buildSectionData(parseItemDetailKeys(fgcItem?.item_detail)));
        setRewardsPurchaseData(buildSectionData(parseItemDetailKeys(rewardsItem?.item_detail)));
        setLoading(false);
      })
      .catch((error) => {
        console.log("error", error);
        setLoading(false);
        setErrorMessage("Unable to load payment methods");
        setErrorAlert(true);
      });
  };

  useEffect(() => {
    const authToken = localStorage.getItem("AuthenticationToken");
    setToken(authToken);
    fetchPaymentMethodsSettings(authToken);
  }, []);

  const handleInputChange = (setData, index, field, value) => {
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], [field]: value };
      return newData;
    });
  };

  // Move the dragged row within the array and re-assign sort (1-based) to match the new order.
  const handleReorder = (setData, evt) => {
    const { oldIndex, newIndex } = evt;
    if (oldIndex === newIndex) return;
    setData((prevData) => {
      const newData = [...prevData];
      const [moved] = newData.splice(oldIndex, 1);
      newData.splice(newIndex, 0, moved);
      return newData.map((item, index) => ({ ...item, sort: index + 1 }));
    });
  };

  const updateSectionSetting = (meta, data) => {
    var myHeaders = new Headers();
    myHeaders.append("Authorization", "Bearer " + token);
    myHeaders.append("cache-control", "no-cache");
    myHeaders.append(
      "Cookie",
      "__cfduid=d5b74235daa6f013fde0aba762ec288811620640208"
    );

    var formdata = new FormData();
    formdata.append("item_id", meta?.item_id);
    formdata.append("path", "settings/update");
    formdata.append("item_type", "settings");
    formdata.append("item_name", meta?.item_name);
    if (meta?.item_group) {
      formdata.append("item_group", meta.item_group);
    }
    formdata.append("item_detail", formatItemDetail(data));
    formdata.append("item_version", meta?.item_version);
    formdata.append("item_status", meta?.item_status);

    var requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formdata,
      redirect: "follow",
      processData: false,
      mimeType: "multipart/form-data",
      async: true,
      crossDomain: true,
    };

    return fetch(`${process.env.REACT_APP_TOKRI_API_URL}/api/frhub`, requestOptions);
  };

  const handleSubmit = () => {
    if (!fgcMeta || !rewardsMeta) {
      setErrorMessage("Payment methods settings not loaded yet");
      setErrorAlert(true);
      return;
    }

    let isValid = true;
    [...fgcPurchaseData, ...rewardsPurchaseData].forEach((item) => {
      if (!item.sort) {
        isValid = false;
        setErrorMessage("Please enter sort value");
        setErrorAlert(true);
      }
    });

    if (!isValid) {
      return;
    }

    setLoader(true);
    Promise.all([
      updateSectionSetting(fgcMeta, fgcPurchaseData),
      updateSectionSetting(rewardsMeta, rewardsPurchaseData),
    ])
      .then((responses) => {
        setLoader(false);
        const allOk = responses.every((response) => response.status == 200);
        if (allOk) {
          setSuccessAlert(true);
          setSuccessMessage("Payment Methods data submitted successfully");
          fetchPaymentMethodsSettings(token);
        } else {
          setErrorMessage("Unable to update Payment Methods");
          setErrorAlert(true);
        }
      })
      .catch((error) => {
        console.log("error", error);
        setLoader(false);
        setErrorMessage("Unable to update Payment Methods");
        setErrorAlert(true);
      });
  };

  const handleCancel = () => {
    fetchPaymentMethodsSettings(token);
  };

  const renderSuccessAlert = () => {
    return (
      <SweetAlert
        title="Success!"
        success
        confirmBtnBsStyle="success"
        onConfirm={() => {
          setSuccessAlert(false);
        }}
        onCancel={() => {
          setSuccessAlert(false);
        }}
      >
        {successMessage ? successMessage : "Submitted"}
      </SweetAlert>
    );
  };

  const renderFailureAlert = () => {
    return (
      <SweetAlert
        title="Warning!"
        warning
        confirmBtnBsStyle="warning"
        onConfirm={() => {
          setErrorAlert(false);
        }}
      >
        {errorMessage ? errorMessage : "Error. Something went wrong."}
      </SweetAlert>
    );
  };

  const renderSection = (sectionTitle, data, setData) => (
    <Card className="mt-3">
      <CardBody>
        <h5 className="mb-3">{sectionTitle}</h5>
        <div className="payment-methods-header">
          <span>Show</span>
          <span>Payment Method</span>
        </div>
        <SortableList onReorder={(evt) => handleReorder(setData, evt)}>
          {data.map((item, index) => (
            <div className="payment-method-item" key={item.method_key}>
              <label className="payment-method-toggle">
                <input
                  type="checkbox"
                  checked={item.is_show}
                  onChange={(e) => {
                    handleInputChange(setData, index, "is_show", e.target.checked);
                  }}
                />
                <span className="payment-method-toggle-slider"></span>
              </label>
              <span className="payment-method-title">{item.Title}</span>
            </div>
          ))}
        </SortableList>
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs breadcrumbItem="Payment Methods" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <div role="status" style={{ textAlign: "center" }}>
                      <CircularProgress size={50} value={100} color={"black"} />
                    </div>
                  ) : (
                    <>
                      <Row>
                        <Col lg={6}>
                          {renderSection("FGC Purchase", fgcPurchaseData, setFgcPurchaseData)}
                        </Col>
                        <Col lg={6}>
                          {renderSection("Rewards Purchase", rewardsPurchaseData, setRewardsPurchaseData)}
                        </Col>
                      </Row>
                      <div
                        style={{ textAlign: "right", marginTop: 20 }}
                        className="btn-sec"
                      >
                        <Button
                          onClick={() => {
                            handleCancel();
                          }}
                          color="danger"
                          type="submit"
                        >
                          Cancel
                        </Button>
                        <button
                          style={{
                            color: "#ffffff",
                            width: "80px",
                            marginLeft: "10px",
                          }}
                          className={"btn btn-success"}
                          onClick={() => {
                            handleSubmit();
                          }}
                          color="success"
                          type="submit"
                        >
                          {loader ? (
                            <CircularProgress
                              size={15}
                              value={100}
                              color={"white"}
                            />
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          {successAlert ? renderSuccessAlert() : null}
          {errorAlert ? renderFailureAlert() : null}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentMethods;
